import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, TableCell } from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';

interface ScrollableTableCellProps {
  children: React.ReactNode;
  width?: string;
}

const ScrollableTableCell: React.FC<ScrollableTableCellProps> = ({ children, width = '50%' }) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkOverflow = useCallback(() => {
    const el = contentRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    checkOverflow();
    const el = contentRef.current;
    if (!el) return;
    const observer = new ResizeObserver(checkOverflow);
    observer.observe(el);
    return () => observer.disconnect();
  }, [checkOverflow]);

  const scroll = (direction: 'left' | 'right') => {
    const el = contentRef.current;
    if (!el) return;
    el.scrollBy({ left: direction === 'left' ? -100 : 100, behavior: 'smooth' });
  };

  return (
    <TableCell sx={{ maxWidth: 0, width, position: 'relative', p: 0 }}>
      {canScrollLeft && (
        <Box
          onClick={(e) => { e.stopPropagation(); scroll('left'); }}
          sx={{
            position: 'absolute', left: 0, top: 0, bottom: 0, width: 24,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'linear-gradient(to right, rgba(255,255,255,0.9), rgba(255,255,255,0))',
            cursor: 'pointer', zIndex: 1,
          }}
        >
          <ChevronLeftIcon sx={{ fontSize: 16, color: 'rgba(0,0,0,0.3)' }} />
        </Box>
      )}
      <Box
        ref={contentRef}
        onScroll={checkOverflow}
        sx={{
          overflowX: 'auto', px: 2, py: 1,
          '&::-webkit-scrollbar': { display: 'none' },
          scrollbarWidth: 'none',
        }}
      >
        {children}
      </Box>
      {canScrollRight && (
        <Box
          onClick={(e) => { e.stopPropagation(); scroll('right'); }}
          sx={{
            position: 'absolute', right: 0, top: 0, bottom: 0, width: 24,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'linear-gradient(to left, rgba(255,255,255,0.9), rgba(255,255,255,0))',
            cursor: 'pointer', zIndex: 1,
          }}
        >
          <ChevronRightIcon sx={{ fontSize: 16, color: 'rgba(0,0,0,0.3)' }} />
        </Box>
      )}
    </TableCell>
  );
};

export default ScrollableTableCell;
